import { Link } from 'react-router-dom';
import { useQueue, useUpdateQueue } from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge';
import { Button } from '@/components/ui/Button';

export const AdminQueuePage = () => {
  const { data: queue, isLoading } = useQueue();
  const updateQueue = useUpdateQueue();

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!queue || queue.length === 0) {
    return (
      <div>
        <h1
          style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Print Queue
        </h1>
        <div
          style={{
            padding: 'var(--space-12)',
            textAlign: 'center',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
          }}
        >
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
            No active orders in the queue.
          </p>
        </div>
      </div>
    );
  }

  const toast = useToast();

  const togglePriority = (orderId: string, current: string) => {
    const next = current === 'rush' ? 'normal' : 'rush';
    updateQueue.mutate([{ orderId, priority: next }], {
      onError: () => toast.error('Failed to update priority'),
    });
  };

  const toggleHold = (orderId: string, currentHold: string | null) => {
    const holdReason = currentHold ? null : 'Manual hold';
    updateQueue.mutate([{ orderId, holdReason }], {
      onError: () => toast.error('Failed to update hold status'),
    });
  };

  // Group by material+color for batch indicators
  const batchGroups = new Map<string, string[]>();
  for (const order of queue) {
    if (order.materialId && order.color) {
      const key = `${order.materialId}-${order.color}`;
      const group = batchGroups.get(key) ?? [];
      group.push(order.orderId);
      batchGroups.set(key, group);
    }
  }
  const batchOrderIds = new Set(
    [...batchGroups.values()].filter((ids) => ids.length > 1).flat(),
  );

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Print Queue
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            {queue.length} order{queue.length !== 1 ? 's' : ''} active
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {queue.map((order, i) => {
          const isBatch = batchOrderIds.has(order.orderId);
          const isHeld = !!order.holdReason;

          return (
            <div
              key={order.orderId}
              className="admin-table-row"
              style={{
                borderRadius: 'var(--radius-lg)',
                border: `1px solid ${isHeld ? '#fecaca' : isBatch ? 'var(--color-primary-200)' : 'var(--border-default)'}`,
                backgroundColor: isHeld ? '#fef2f2' : 'var(--bg-surface)',
                padding: 'var(--space-3) var(--space-4)',
              }}
            >
              {/* Top row: position + order info + badges */}
              <div className="flex items-center" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                {/* Position + arrows */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1px',
                    flexShrink: 0,
                  }}
                >
                  <button
                    onClick={() => {
                      if (i > 0) updateQueue.mutate([{ orderId: order.orderId, queuePosition: i - 1 }]);
                    }}
                    disabled={i === 0}
                    className="admin-queue-arrow"
                    aria-label="Move up"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '22px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-default)',
                      backgroundColor: 'var(--bg-surface)',
                      cursor: i === 0 ? 'default' : 'pointer',
                      opacity: i === 0 ? 0.25 : 1,
                      color: 'var(--text-secondary)',
                      padding: 0,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M2 6.5L5 3.5L8 6.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--text-tertiary)',
                      lineHeight: 1,
                      padding: '2px 0',
                    }}
                  >
                    {i + 1}
                  </span>
                  <button
                    onClick={() => {
                      if (i < queue.length - 1) updateQueue.mutate([{ orderId: order.orderId, queuePosition: i + 1 }]);
                    }}
                    disabled={i === queue.length - 1}
                    className="admin-queue-arrow"
                    aria-label="Move down"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '22px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-default)',
                      backgroundColor: 'var(--bg-surface)',
                      cursor: i === queue.length - 1 ? 'default' : 'pointer',
                      opacity: i === queue.length - 1 ? 0.25 : 1,
                      color: 'var(--text-secondary)',
                      padding: 0,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M2 3.5L5 6.5L8 3.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Order info */}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="flex items-center" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <Link
                      to={`/admin/orders/${order.orderId}`}
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--text-accent)',
                        textDecoration: 'none',
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    >
                      #{order.orderId.slice(-8).toUpperCase()}
                    </Link>
                    <OrderStatusBadge status={order.status} />
                    {order.priority === 'rush' && (
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 'var(--font-weight-bold)',
                          color: '#92400e',
                          backgroundColor: '#fef3c7',
                          padding: '1px 6px',
                          borderRadius: 'var(--radius-full)',
                          letterSpacing: '0.05em',
                        }}
                      >
                        RUSH
                      </span>
                    )}
                    {isBatch && (
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 'var(--font-weight-bold)',
                          color: 'var(--color-primary-700)',
                          backgroundColor: 'var(--color-primary-50)',
                          padding: '1px 6px',
                          borderRadius: 'var(--radius-full)',
                          letterSpacing: '0.05em',
                        }}
                      >
                        BATCH
                      </span>
                    )}
                  </div>
                  <div
                    className="flex items-center"
                    style={{
                      gap: 'var(--space-3)',
                      marginTop: '3px',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    {order.materialId && (
                      <span>
                        {order.materialId.toUpperCase()}
                        {order.color ? ` / ${order.color}` : ''}
                      </span>
                    )}
                    {isHeld && (
                      <span style={{ color: '#991b1b', fontWeight: 'var(--font-weight-medium)' }}>
                        HOLD: {order.holdReason}
                      </span>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center" style={{ gap: 'var(--space-1)', flexShrink: 0 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePriority(order.orderId, order.priority)}
                    style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }}
                  >
                    {order.priority === 'rush' ? 'Unrush' : 'Rush'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleHold(order.orderId, order.holdReason ?? null)}
                    style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }}
                  >
                    {isHeld ? 'Release' : 'Hold'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
